/**
 * @fileoverview Prompt template for the single-chapter-extractor-v2 agent.
 *
 * This prompt analyzes short-form fiction content and extracts chapter-level
 * metadata: narrative goals, pacing, POV, and scene breakdown.
 *
 * Note: Plot extraction (summary + writtenPlot) is handled separately by
 * plot-extractor-v2. Key events are derived from writtenPlot (single source
 * of truth for short-form). This agent focuses on chapter metadata and scenes.
 */

export const prompt = `
You are an expert story analyst specializing in short-form fiction.

## TASK INSTRUCTIONS:
Your task is to analyze the following short story and extract chapter-level metadata and scene breakdown. Short-form fiction (Flash Fiction under 1,000 words, Short Stories 1,000-7,500 words) has unique characteristics:

- **Singular focus:** One central conflict, limited subplots
- **Economy of character:** Small cast, typically 1-3 main characters
- **Tight pacing:** Every scene serves the core narrative
- **Unity of effect:** All elements contribute to a single emotional impact

Analyze the text with these principles in mind.

**DOCUMENT TO ANALYZE**
{{{textInput}}}

---

## OUTPUT INSTRUCTIONS:
You MUST provide your output in the required JSON format, adhering strictly to the schema.

Since this is short-form fiction, the entire story is treated as a single chapter.

### PART 1: Chapter Metadata

Note: Key events (keyEvents) are NOT extracted here — they are derived from the plot outline extracted by a separate agent.

1. **chapter.chapterGoals**: Analyze the narrative goals:
    - \`plotDevelopment\`: How the story moves from beginning to resolution. 1-2 sentences.
    - \`charDevelopment\`: Key character moments and growth. 1-2 sentences.
    - \`stakesAndTension\`: How tension escalates to the climax. 1-2 sentences.
2. **chapter.pacing**: Assess the overall pacing ('Slow & Reflective', 'Moderate & Developing', or 'Fast & Action-Packed').
3. **chapter.projectedWordCount**: Estimate the word count based on the text length.
4. **chapter.povCharacterName**: If identifiable, name the POV character.
5. **chapter.povJustification**: Explain why this character serves as the POV.

### PART 2: Scene Breakdown

Identify the individual scenes within the story. A scene is a continuous unit of action that occurs in one location and time. Look for scene breaks indicated by:
- **Location changes**: Characters move to a new setting
- **Time jumps**: Significant time passes (hours, days, or more)
- **POV shifts**: The perspective changes to a different character
- **Narrative breaks**: Visual breaks in the text (extra whitespace, symbols like "* * *")
- **Tonal shifts**: A clear change in mood or dramatic focus

For each scene, extract:
8. **chapter.scenes[].sceneNumber**: Sequential number (1, 2, 3...)
9. **chapter.scenes[].title**: A brief, descriptive title capturing the scene's focus
10. **chapter.scenes[].keyBeats**: The key story beats and moments that occur in this scene

### IMPORTANT NOTES:
- Plot and character extraction are handled by separate agents; focus on chapter metadata and scenes.
- For POV, note if the story uses first-person, third-person limited, or omniscient narration.
- Every story must have at least one scene; most short stories have 2-5 scenes.
- Scene numbers should start from 1 and be sequential.
`;
